import React from "react";
import { TableRow, TableCell, Chip, Button, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { IOwnerObject } from "../../../../services/api.types";
import ownerService from "../../../../services/owner.service";
import { useRole } from "../../../../context/RoleContext";

type Props = {
  item: IOwnerObject;
  handleClick: (id: string) => void;
  // Re-fetch both lists after an action resolves so the owner moves tabs.
  onActionComplete: () => void;
  onError: (message: string) => void;
};

type CountdownInfo = {
  label: string;
  color: "success" | "warning" | "error" | "default";
};

// Phase 26 countdown (D-08): before Start the deadline is pendingAssignedAt + 24h;
// after Start it is pendingStartedAt + 48h. Color: green >=12h, amber 4-12h, red <4h.
function computeCountdown(item: IOwnerObject, nowMs: number): CountdownInfo {
  const base = item.pendingStartedAt
    ? new Date(item.pendingStartedAt).getTime() + 48 * 3600 * 1000
    : item.pendingAssignedAt
    ? new Date(item.pendingAssignedAt).getTime() + 24 * 3600 * 1000
    : null;

  if (base === null || Number.isNaN(base)) {
    return { label: "—", color: "default" };
  }

  const remainingMs = base - nowMs;
  if (remainingMs <= 0) {
    return { label: "Expired", color: "error" };
  }

  const totalMinutes = Math.floor(remainingMs / 60000);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;

  let color: CountdownInfo["color"];
  if (h >= 12) {
    color = "success";
  } else if (h >= 4) {
    color = "warning";
  } else {
    color = "error";
  }

  return { label: `${h}h ${m}m`, color };
}

const PendingTableRow = ({
  item,
  handleClick,
  onActionComplete,
  onError,
}: Props) => {
  const { t } = useTranslation();
  const { isSuperAdmin } = useRole();
  const [busy, setBusy] = React.useState(false);
  // Tick once a minute so the countdown stays roughly current without churn.
  const [nowMs, setNowMs] = React.useState(() => Date.now());

  React.useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  const countdown = computeCountdown(item, nowMs);
  const isStarted = !!item.pendingStartedAt;

  const runAction = async (
    action: (id: string) => Promise<unknown>,
    failMessage: string
  ) => {
    setBusy(true);
    try {
      await action(item._id);
      onActionComplete();
    } catch (err: any) {
      onError(err?.message || failMessage);
    } finally {
      setBusy(false);
    }
  };

  // Stop row-click navigation when interacting with action buttons.
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <TableRow
      hover
      onClick={() => handleClick(item._id)}
      tabIndex={-1}
      sx={{ cursor: "pointer" }}
    >
      <TableCell align="left">{`${item.firstName} ${item.lastName}`}</TableCell>
      <TableCell align="left">{`${item.country_code} ${item.phone}`}</TableCell>
      <TableCell align="left">
        <Chip label={countdown.label} color={countdown.color} size="small" />
      </TableCell>
      <TableCell align="left" onClick={stop}>
        <Stack direction="row" spacing={1}>
          {/* "Start" is the ASSIGNED RM claiming their 24h SLA window — it stamps
              pendingStartedAt, and POST /owners/:id/start stays restricted to
              relationship_manager for that reason. A super_admin pressing it would
              start a clock measured against someone else's deadline, and would get
              a 403 besides. Accept/reject require no prior start, so a super_admin
              sees the decisions directly and loses no capability. */}
          {!isStarted && !isSuperAdmin ? (
            <Button
              variant="contained"
              size="small"
              disabled={busy}
              onClick={() =>
                runAction(
                  (id) => ownerService.startOwner(id),
                  "Failed to start owner"
                )
              }
            >
              {t("common.start")}
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                color="success"
                size="small"
                disabled={busy}
                onClick={() =>
                  runAction(
                    (id) => ownerService.acceptOwner(id),
                    "Failed to accept owner"
                  )
                }
              >
                {t("common.approve")}
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                disabled={busy}
                onClick={() =>
                  runAction(
                    (id) => ownerService.rejectOwner(id),
                    "Failed to reject owner"
                  )
                }
              >
                {t("common.reject")}
              </Button>
            </>
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );
};

export default PendingTableRow;
